import React, { createContext, useEffect, useState, useCallback, useRef } from 'react';
import ServerCall from '../game/serverCall';
import { API_CONSTANTS, GAME_SERVICE_API_BASE } from '../game/common/api_constants';
import { detectLane } from '../game/router/basePath';

export const LaunchContext = createContext(null);

const LAUNCH_PARAM_KEYS = [
  'clientToken',
  'mode',
  'gameId',
  'languageCode',
  'platformType',
  'isFreePlay',
  'currencyCode',
  'username',
  'playerId',
  'brandId',
  'cashierUrl',
  'homeUrl',
  'historyUrl',
  'additionalInfo',
  'tableId',
  'tier',
  'mockSession',
];

// The operator lane comes from the URL PATH, not the query string, so it is
// read separately from LAUNCH_PARAM_KEYS (which is the query-param contract with
// the operator). See `game/router/basePath.js`.
function parseLaunchParams(search, pathname) {
  const params = new URLSearchParams(search);
  const result = {};
  for (const key of LAUNCH_PARAM_KEYS) {
    const value = params.get(key);
    if (value !== null) {
      result[key] = decodeURIComponent(value);
    }
  }
  // NHLFS appends its one-time launch token as `token`, not `clientToken`.
  if (result.clientToken === undefined && params.get('token') !== null) {
    result.clientToken = decodeURIComponent(params.get('token'));
  }
  if (result.isFreePlay !== undefined) {
    result.isFreePlay = result.isFreePlay === 'true';
  }
  if (result.mockSession !== undefined) {
    result.mockSession = result.mockSession === 'true';
  }
  const lane = detectLane(pathname);
  if (lane) {
    result.lane = lane;
  }
  return result;
}

function sendPostMessage(method, data) {
  try {
    const payload = data ? { method, data } : { method };
    window.parent.postMessage(JSON.stringify(payload), '*');
  } catch {
    // ignore if no parent frame
  }
}

/**
 * LaunchProvider — manages the DraftKings Casino API game launch flow.
 *
 * Wallet operations are server-driven (rivalz-games-backend → rivalz-games-services
 * → DK). The React shell no longer moves money.
 *
 * Provides via context:
 *   - launchParams : parsed URL query parameters
 *   - session      : InitSession response (sessionId, balance, etc.)
 *   - isReady      : true once session is established
 *   - isLoading    : true while InitSession is in flight
 *   - error        : error string or null
 *   - balance      : player balance in cents
 *   - gemsBalance  : player gems balance in whole units (not divided by 100)
 *   - refreshBalance(): re-reads the operator balance from rivalz-games-services
 *   - displayName  : player username from launch params
 */
export function LaunchProvider({ children }) {
  const [launchParams, setLaunchParams] = useState(null);
  const [session, setSession] = useState(null);
  const [balance, setBalance] = useState(null);
  const [gemsBalance, setGemsBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const initCalled = useRef(false);

  useEffect(() => {
    if (session?.balance !== undefined) {
      setBalance(session.balance);
    }
  }, [session]);

  // GET /wallet runs after InitSession so the axios interceptor in
  // serverCall.jsx can attach the JWT that was just stored in localStorage.
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await ServerCall.get(API_CONSTANTS.wallet);
        const value = resp.data?.data?.balances?.gems?.value;
        if (!cancelled && value != null) {
          setGemsBalance(value);
        }
      } catch {
        // keep last-known value on failure
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Re-reads the live operator (cash) balance from rivalz-games-services,
  // keyed on the InitSession sessionId. The server moves the money; this only
  // refreshes the display. A newer call supersedes any still in flight so a
  // stale response can't overwrite it; a failure keeps the last-known value.
  const sessionId = session?.sessionId;
  const balanceRequestSeq = useRef(0);
  const refreshBalance = useCallback(async () => {
    if (!sessionId) return;
    const seq = ++balanceRequestSeq.current;
    try {
      const resp = await fetch(`${GAME_SERVICE_API_BASE}/api/wallet/getBalance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (seq === balanceRequestSeq.current && data.balance != null) {
        setBalance(data.balance);
      }
    } catch {
      // keep last-known value on failure
    }
  }, [sessionId]);

  // --- InitSession on mount ---
  useEffect(() => {
    const params = parseLaunchParams(
      window.location.search,
      window.location.pathname,
    );
    setLaunchParams(params);

    if (!params.clientToken || !params.gameId) {
      setError('Missing clientToken or gameId in launch URL');
      setIsLoading(false);
      sendPostMessage('GAME_ERROR', {
        code: 'BAD_REQUEST',
        message: 'Missing clientToken or gameId in launch URL.',
        action: 'lobby',
      });
      return;
    }

    if (initCalled.current) return;
    initCalled.current = true;

    const callInitSession = async () => {
      try {
        sendPostMessage('GAME_LOAD', { progress: 10 });

        let playerIp;
        try {
          const ipResp = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResp.json();
          playerIp = ipData.ip;
        } catch {
          // Falls back to server-detected IP
        }

        const resp = await fetch(`${GAME_SERVICE_API_BASE}/api/wallet/initSession`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientToken: params.clientToken,
            gameId: params.gameId,
            brandId: params.brandId || undefined,
            additionalInfo: params.additionalInfo || undefined,
            username: params.username,
            playerIp,
            mockSession: params.mockSession === true,
            // Omitted on a default-lane launch so the backend takes its own
            // default rather than being told one.
            lane: params.lane || undefined,
          }),
        });

        const data = await resp.json();

        if (!resp.ok || (data.error && data.error !== 'OK' && data.error !== '')) {
          const errorCode = data.error || 'INTERNAL_ERROR';
          setError(errorCode);
          setIsLoading(false);
          sendPostMessage('GAME_ERROR', {
            code: errorCode,
            message: `Session initialization failed: ${errorCode}`,
            action: 'lobby',
          });
          return;
        }

        // Store the Low6 JWT minted by rivalz-games-backend via /auth/exchange.
        // serverCall.jsx's axios interceptor reads localStorage.token on every
        // request, so this is the only handoff needed for subsequent auth.
        if (data.jwt) {
          localStorage.setItem('token', data.jwt);
        }

        sendPostMessage('GAME_LOAD', { progress: 80 });
        setSession(data);
        setBalance(data.balance);
        setIsLoading(false);
        sendPostMessage('GAME_READY');
      } catch (err) {
        setError('INTERNAL_ERROR');
        setIsLoading(false);
        sendPostMessage('GAME_ERROR', {
          code: 'INTERNAL_ERROR',
          message: 'Failed to initialize game session.',
          action: 'lobby',
        });
      }
    };

    callInitSession();
  }, []);

  const navigateToCashier = useCallback(() => {
    if (launchParams?.cashierUrl) {
      window.open(launchParams.cashierUrl, '_blank');
    }
  }, [launchParams]);

  const navigateToHome = useCallback(() => {
    if (launchParams?.homeUrl) {
      window.location.href = launchParams.homeUrl;
    }
  }, [launchParams]);

  const value = {
    launchParams,
    session,
    isReady: !!session,
    isLoading,
    error,
    balance,
    gemsBalance,
    // Session currency as reported by the operator (DK: USD; Pixel: GEM/RCR).
    // Drives lobby filtering and provider-specific currency icons.
    currencyCode: session?.currency ?? launchParams?.currencyCode ?? null,
    displayName:
      launchParams?.username || session?.user?.username || session?.playerId || null,
    isLoggedIn: !!session,

    // Re-read the operator balance (after entry / payout).
    refreshBalance,

    // Navigation
    navigateToCashier,
    navigateToHome,
  };

  return (
    <LaunchContext.Provider value={value}>
      {children}
    </LaunchContext.Provider>
  );
}
